import React from 'react';
import LinkedItemsCard from './LinkedItemsCard';
import ActionsMenu from '../../../../../components/ActionsMenu';
import { getFamilyShareInfo, idsMatch } from '../../../../ProjectDashboard/utils/projectShareUtils';
import { useToolAccess } from '../../../../../tools/ToolAccessContext';

/**
 * LinkedProjects component
 * Displays projects linked to the current family, with share / unshare / unlink actions.
 */
export default function LinkedProjects({
    family,
    projects,
    activeProjectId,
    onSelectProject,
    onShareFamily,
    onUnshareFamily,
    onUnlinkFamily,
    onLinkToActive
}) {
    const { canEditCurrent } = useToolAccess();
    const items = projects || [];
    const shareInfo = family ? getFamilyShareInfo(family.id, items) : { isShared: false };
    const linkedToActive = items.some(p => idsMatch(p.id, activeProjectId));

    return (
        <LinkedItemsCard icon="fa-solid fa-diagram-project" title="Linked Projects" count={items.length}>
            {canEditCurrent && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => onShareFamily?.(family)}
                        style={{ margin: 0, padding: '0.35rem 0.7rem', fontSize: '0.8rem' }}
                    >
                        <i className="fa-solid fa-share-nodes" style={{ marginRight: '0.35rem' }}></i>
                        Share with project
                    </button>
                    {activeProjectId && !linkedToActive && (
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => onLinkToActive?.(family)}
                            style={{ margin: 0, padding: '0.35rem 0.7rem', fontSize: '0.8rem' }}
                        >
                            <i className="fa-solid fa-link" style={{ marginRight: '0.35rem' }}></i>
                            Link to active project
                        </button>
                    )}
                </div>
            )}
            {items.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {items.map(p => {
                        const isActive = idsMatch(p.id, activeProjectId);
                        return (
                            <div
                                key={p.id}
                                className={`linked-item-row clickable-row${isActive ? ' is-selected' : ''}`}
                                onClick={() => onSelectProject?.(p)}
                                onKeyDown={(event) => {
                                    if (event.target !== event.currentTarget) return;
                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                    event.preventDefault();
                                    onSelectProject?.(p);
                                }}
                                tabIndex={0}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid var(--border)',
                                    fontSize: '0.85rem',
                                    width: '100%',
                                    gap: '0.5rem'
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                                    <span className="row-title">{p.name}</span>
                                    {isActive && (
                                        <span className="row-muted" style={{ fontSize: '0.7rem' }}>Active</span>
                                    )}
                                    {shareInfo.isShared && (
                                        <i className="fa-solid fa-share-nodes" title="Shared across projects" style={{ fontSize: '0.75rem' }}></i>
                                    )}
                                </span>
                                {canEditCurrent && (
                                    <ActionsMenu
                                        compact
                                        label={`${p.name} project link actions`}
                                        items={[
                                            {
                                                label: 'Switch to this project',
                                                icon: 'fa-solid fa-diagram-project',
                                                onSelect: () => onSelectProject?.(p)
                                            },
                                            {
                                                label: 'Unshare',
                                                icon: 'fa-solid fa-code-branch',
                                                disabled: !shareInfo.isShared,
                                                onSelect: () => onUnshareFamily?.(family, p, shareInfo)
                                            },
                                            {
                                                label: 'Remove from project',
                                                icon: 'fa-solid fa-unlink',
                                                danger: true,
                                                onSelect: () => onUnlinkFamily?.(family, p)
                                            }
                                        ]}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p style={{
                    fontStyle: 'italic',
                    color: 'var(--text-secondary)',
                    fontSize: '0.85rem',
                    margin: 0
                }}>
                    No projects currently link this family.
                </p>
            )}
        </LinkedItemsCard>
    );
}
